/*
 * Styles the Shopify Forms "Business account" form on the B2B page
 * (sections/b2b-account.liquid). The app renders into an open shadow root,
 * so these rules are adopted into that root. Selectors match the app's
 * class-name prefixes (e.g. [class*="_formInputField_"]) rather than its
 * hashed suffixes, and are scoped with :host so they outrank the app's own
 * rules. If the app's markup changes, the form falls back to the colours set
 * on the host in the section stylesheet.
 */
(() => {
  const CSS = `
:host { text-align: center; }

/* Header: large, tight headline with a quiet subtitle, outside the card */
:host [class*="_formHeader_"] { margin: 0 auto 40px !important; max-width: 600px; }
:host h2[class*="_textHeading_"] {
  margin: 0 0 14px;
  font: 700 clamp(34px, 5.2vw, 56px) / 1.05 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
:host [class*="_formHeader_"] [class*="_textBody_"] {
  font-size: clamp(17px, 1.7vw, 21px);
  line-height: 1.45;
  text-wrap: balance;
}

/* The form is the card: two-column grid so first/last name sit side by side */
:host form[class*="_formFieldset_"] {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-flow: row dense;
  gap: 14px;
  padding: 44px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  background:
    radial-gradient(70% 60% at 100% 0%, rgba(43, 108, 255, 0.12), transparent 70%),
    linear-gradient(#1d1d1f, #18181a);
  text-align: start;
}
:host form[class*="_formFieldset_"] > * { grid-column: 1 / -1; min-width: 0; }
:host form[class*="_formFieldset_"] > :empty:not(input) { display: none; }
:host form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name),
:host form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name) + output { grid-column: 1; }
:host form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#last_name),
:host form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#last_name) + output { grid-column: 2; }

/* "Company billing address" becomes a section title with a hairline above */
:host form[class*="_formFieldset_"] > legend {
  margin-top: 14px;
  padding: 28px 0 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #f5f5f7;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3 !important;
  letter-spacing: -0.012em;
}

:host [class*="_multiFieldLine_"],
:host [class*="_nonMultineFieldLine_"] { gap: 14px; }
:host [class*="_multiFieldLine_"] { display: flex; width: 100%; }
:host [class*="_fieldContainer_"] { min-width: 0; }
:host [class*="_formFieldContainer_"] { text-align: start; }

/* Fields */
:host input[class*="_formInputField_"],
:host input[class*="_formPhoneInputField_"] {
  box-sizing: border-box;
  width: 100%;
  min-height: 56px;
  padding-inline: 18px;
  font-size: 16px;
  line-height: 20px;
  caret-color: #2997ff;
  transition: box-shadow 200ms ease, background-color 200ms ease;
}
@media (hover: hover) and (pointer: fine) {
  :host input[class*="_formInputField_"]:hover:not(:focus),
  :host input[class*="_formPhoneInputField_"]:hover:not(:focus),
  :host button[class*="_selectToggle_"]:hover:not(:focus-visible) {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  }
}
:host input[class*="_formInputField_"]:focus,
:host input[class*="_formPhoneInputField_"]:focus {
  box-shadow: var(--form-field-border-focused);
  background-color: #29292c;
}
:host input:-webkit-autofill,
:host input:-webkit-autofill:hover,
:host input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f5f5f7;
  -webkit-box-shadow: var(--form-field-border), 0 0 0 1000px #262628 inset;
  transition: background-color 9999s ease 0s;
}
:host input[class*="_formInputField_"]:-webkit-autofill ~ label,
:host input[class*="_formPhoneInputField_"]:-webkit-autofill ~ label { color: #86868b; }

:host [class*="_formInputFieldLabel_"] {
  top: 19px !important;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  left: 18px;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.01em;
}

/* Errors */
:host div[class*="_withError_"] > input { box-shadow: inset 0 0 0 1px var(--form-error-color), 0 0 0 4px rgba(255, 105, 97, 0.2); }
:host output[class*="_formError_"] {
  margin-top: -6px;
  padding-inline: 4px;
  font-size: 13px;
  line-height: 1.4;
  text-align: start;
}

/* Selects (country, state, phone country) */
:host button[class*="_selectToggle_"] {
  box-sizing: border-box;
  min-height: 56px;
  max-height: none;
  padding: 0 18px;
  font-size: 16px;
  text-align: start;
  transition: box-shadow 200ms ease;
}
:host button[class*="_selectToggle_"][aria-expanded="true"] { box-shadow: var(--form-field-border-focused); }
:host [class*="_selectToggleText_"] { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
:host [class*="_formAddressRegionPlaceholder_"] { color: #86868b; }
:host [class*="_arrowDownIcon_"] { display: inline-flex; flex: none; margin: 0; margin-inline-start: 12px; }
:host [class*="_arrowDownIcon_"] svg { display: none; }
:host [class*="_arrowDownIcon_"]::after {
  content: "";
  width: 12px;
  height: 12px;
  background: #86868b;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2 4.25 6 8l4-3.75' fill='none' stroke='%23000' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / contain no-repeat;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2 4.25 6 8l4-3.75' fill='none' stroke='%23000' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / contain no-repeat;
}
:host [class*="_flagIcon_"] { width: 24px; height: 18px; border-radius: 3px; object-fit: cover; }

:host [class*="_dropdownContainer_"] {
  z-index: 20;
  margin-top: 6px;
  border-radius: 14px;
  background-color: #2a2a2d !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 20px 44px rgba(0, 0, 0, 0.55);
}
:host [class*="_optionList_"] { padding: 6px; background: transparent; scrollbar-color: rgba(255, 255, 255, 0.25) transparent; }
:host [class*="_optionListItem_"] { margin-bottom: 2px; border-radius: 8px; box-shadow: none; }
:host [class*="_optionListItem_"] > div { padding: 10px 12px; border-radius: 8px; font-size: 15px; text-align: start; }
:host [class*="_optionListItem_"]:hover,
:host [class*="_optionListItem_"] > div:focus { background-color: rgba(255, 255, 255, 0.08); outline: none; }
:host [class*="_optionListItem_"] > div:focus-visible { outline: 2px solid #2997ff; outline-offset: -2px; }
:host [class*="_optionListItemSelected_"],
:host [class*="_optionListItemSelected_"] > div { background-color: #0071e3 !important; color: #fff; outline: none !important; }

:host [class*="_formPhoneInputContainer_"] { grid-template-columns: minmax(0, 104px) minmax(0, 1fr); gap: 14px; width: 100%; }

/* Submit */
:host button[class*="_formSubmitButton_"] {
  box-sizing: border-box;
  align-items: center;
  min-height: 54px;
  margin-top: 10px;
  font: 600 17px / 1.2 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: -0.01em;
  touch-action: manipulation;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), background-color 220ms ease, filter 220ms ease;
}
@media (hover: hover) and (pointer: fine) {
  :host button[class*="_formSubmitButton_"]:hover { background-color: #0068d6; filter: none; }
}
:host button[class*="_formSubmitButton_"]:active { transform: scale(0.98); transition-duration: 120ms; }

/* Fine print under the card */
:host div[class*="_formDisclaimer_"] {
  max-width: 560px;
  margin: 24px auto 0 !important;
  color: #86868b;
  font-size: 12px;
  line-height: 1.6;
}
:host div[class*="_formDisclaimer_"] p + p { margin-top: 4px; }
:host div[class*="_formDisclaimer_"] strong { color: #d2d2d7; font-weight: 600; }
:host div[class*="_formDisclaimer_"] a { color: #2997ff; text-decoration: underline; text-underline-offset: 3px; }

/* Right-to-left storefront languages. Text the app hasn't translated stays
   English; plaintext keeps its full stop at the end of the sentence. */
:host([dir="rtl"]) [class*="_formInputFieldLabel_"] { left: auto; right: 18px; transform-origin: right top; }
:host([dir="rtl"]) :is(h2[class*="_textHeading_"], [class*="_formHeader_"] p, [class*="_formDisclaimer_"] p) { unicode-bidi: plaintext; }
:host([dir="rtl"]) form[class*="_formFieldset_"] > legend { unicode-bidi: plaintext; text-align: right; }

@media (max-width: 600px) {
  :host [class*="_formHeader_"] { margin-bottom: 28px !important; }
  :host form[class*="_formFieldset_"] { gap: 12px; padding: 22px 18px; border-radius: 22px; }
  :host [class*="_multiFieldLine_"],
  :host [class*="_nonMultineFieldLine_"],
  :host [class*="_formPhoneInputContainer_"] { gap: 12px; }
  :host form[class*="_formFieldset_"] > legend { margin-top: 10px; padding-top: 22px; font-size: 17px; }
  :host [class*="_formPhoneInputContainer_"] { grid-template-columns: minmax(0, 92px) minmax(0, 1fr); }
  /* City, state, ZIP: city on its own row, state and ZIP share the next */
  :host [class*="_multiFieldLine_"]:has(> :nth-child(3)) { flex-wrap: wrap; }
  :host [class*="_multiFieldLine_"]:has(> :nth-child(3)) > :first-child { flex-basis: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  :host input, :host button { transition: none !important; }
  :host button[class*="_formSubmitButton_"]:active { transform: none; }
}
`;

  let sheet = null;
  const supportsAdopted = 'adoptedStyleSheets' in Document.prototype && 'replaceSync' in CSSStyleSheet.prototype;
  const watched = new WeakSet();

  const ensureSheet = (root) => {
    if (supportsAdopted) {
      if (!sheet) {
        sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
      }
      const sheets = root.adoptedStyleSheets;
      // Keep ours last so it wins over sheets the app adds later.
      if (sheets[sheets.length - 1] !== sheet) {
        root.adoptedStyleSheets = [...sheets.filter((s) => s !== sheet), sheet];
      }
    } else if (!root.querySelector('style[data-b2b-account]')) {
      const style = document.createElement('style');
      style.dataset.b2bAccount = '';
      style.textContent = CSS;
      root.append(style);
    }
  };

  const styleHost = (host) => {
    const root = host.shadowRoot;
    if (!root) {
      setTimeout(() => styleHost(host), 100);
      return;
    }
    host.setAttribute('dir', getComputedStyle(host).direction === 'rtl' ? 'rtl' : 'ltr');
    ensureSheet(root);
    if (!watched.has(root)) {
      watched.add(root);
      new MutationObserver(() => ensureSheet(root)).observe(root, { childList: true, subtree: true });
    }
    requestAnimationFrame(() => host.setAttribute('data-b2b-styled', ''));
  };

  const scan = (scope) => scope.querySelectorAll('shopify-forms-embed').forEach(styleHost);

  const init = (section) => {
    if (!section || section.dataset.b2bBound === 'true') return;
    section.dataset.b2bBound = 'true';
    scan(section);
    // The app mounts its element and shadow root after load; catch it when it lands.
    new MutationObserver(() => scan(section)).observe(section, { childList: true, subtree: true });
  };

  const initAll = () => document.querySelectorAll('[data-b2b-account]').forEach(init);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
